import { useAssistantStream } from '@/components/assistant/useAssistantStream';
import { AssistantPanel, AssistantFab } from '@/components/assistant/AssistantChat';

const SALES_ENDPOINT = '/api/assistant/sales/stream';

const OPPORTUNITY_SUGGESTIONS = [
  'Summarise this opportunity',
  "What's the next best step?",
  'Draft a follow-up email',
] as const;

const PIPELINE_SUGGESTIONS = [
  'What should I work on next?',
  'Which deals are going stale?',
  'Summarise my pipeline',
] as const;

/**
 * Side-rail AI helper on the opportunity detail page — the sales counterpart to
 * `TicketAssistant`. The server assembles the opportunity context (stage, value, notes,
 * next step, owner) under the user's impersonation; only the id is sent from here.
 */
export function OpportunityAssistant({ opportunityId }: { opportunityId: number }) {
  const chat = useAssistantStream({
    endpoint: SALES_ENDPOINT,
    body: () => ({ mode: 'opportunity', opportunityId }),
    resetKey: opportunityId,
  });

  return (
    <AssistantPanel
      chat={chat}
      title="Sales Assistant"
      suggestions={OPPORTUNITY_SUGGESTIONS}
      suggestionsLabel="Ask about this opportunity:"
      placeholder="Ask about this opportunity…"
    />
  );
}

/**
 * Floating AI helper for the opportunity list pages (Open Opps / My Opps).
 *
 * Both pages send the same pipeline context — active opportunities with the current user's
 * own deals flagged — so "what should I work on next?" answers personally from either page
 * while the wider team pipeline stays visible. `scope` mirrors the page's `assignedTo` prop
 * and only decides how the server frames the list, not who can see what.
 */
export function PipelineAssistant({ scope }: { scope: 'me' | 'any' }) {
  const chat = useAssistantStream({
    endpoint: SALES_ENDPOINT,
    body: () => ({ mode: 'pipeline', scope }),
    resetKey: scope,
  });

  return (
    <AssistantFab
      chat={chat}
      title="Sales Assistant"
      label="Ask AI"
      suggestions={PIPELINE_SUGGESTIONS}
      suggestionsLabel="Ask about your pipeline:"
      placeholder="Ask about your pipeline…"
    />
  );
}
